import React from "react";
import { styleFor } from "./registry";
import { attachmentKind, cover } from "./frame";

const INPUT =
  "w-full px-[11px] py-[8px] rounded-[8px] font-body text-[15px] bg-white dark:bg-neutral-800 border border-dupontGoldLt/40 " +
  "text-dupontBlue dark:text-darkText placeholder:italic placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-dupontBlue dark:focus:ring-dupontGoldLt";

const LABEL = "font-blairBody text-[12px] font-semibold tracking-[0.04em] text-gray-600 dark:text-gray-400";

/**
 * How to build the chosen style: one plain line of directions, then only the
 * boxes that style needs.
 *
 * Anything written per photo — a Pop-Up Book page, an Album Page line, a name
 * and label in a line-up — sits NEXT TO the photo it belongs to, so there is
 * never any doubt which box goes with which picture (Sam, 2026-09-15: "how do i
 * edit each page... clear but concise directions").
 */
function StyleFields({ style, details, attachments, onChange }) {
  const s = styleFor(style);
  const photos = (attachments || []).filter((a) => attachmentKind(a) === "image");
  const set = (key, value) => onChange({ ...details, [key]: value });

  const fields = s.fields || [];
  const lineKey = s.pages ? "pages" : s.lines ? "lines" : null;   // one line of text per photo
  const lineMax = lineKey === "pages" ? s.pages : s.lines;
  const lineLabel = lineKey === "pages" ? "Page" : "Line for photo";
  const perPhoto = lineKey || s.people ? photos.length : 0;

  const writeLine = (i, value) => {
    const written = photos.map((_, j) => (details[lineKey] || [])[j] || "");
    written[i] = value;
    onChange({ ...details, [lineKey]: written });
  };
  const writePerson = (i, key, value) => {
    const people = photos.map((_, j) => ({ name: "", label: "", ...(details.people || [])[j] }));
    people[i] = { ...people[i], [key]: value };
    onChange({ ...details, people });
  };

  const needsNothing = !fields.length && !lineKey && !s.people;
  if (needsNothing && !s.how) return null;

  return (
    <div className="mt-[11px] grid gap-[10px] rounded-[10px] bg-dupontCream/60 dark:bg-neutral-800/60 p-[12px]">
      {s.how && (
        <p className="m-0 font-marginNote font-semibold text-[15px] leading-[1.35] text-dupontBlue dark:text-darkText">
          <b className="mr-[8px] font-blairBody text-[12px] tracking-[0.14em] uppercase text-dupontGoldDk">How to build it</b>
          {s.how}
        </p>
      )}

      {fields.map((f) => {
        const id = `style-${s.key}-${f.key}`;
        const Field = f.long ? "textarea" : "input";
        return (
          <label key={f.key} htmlFor={id} className="grid gap-[3px]">
            <span className={LABEL}>{f.label}{f.need ? " *" : ""}</span>
            <Field id={id} aria-label={f.label} className={INPUT} maxLength={f.max} placeholder={f.placeholder}
                   rows={f.long ? 2 : undefined} value={details[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
          </label>
        );
      })}

      {(lineKey || s.people) && perPhoto === 0 && (
        <p className="m-0 font-blairBody text-[13px] text-gray-500">
          Add your photos above and a box will appear here for each one.
        </p>
      )}

      {photos.map((photo, i) => {
        if (!lineKey && !s.people) return null;
        const number = lineKey ? `${lineLabel} ${i + 1}` : `Person ${i + 1}`;
        return (
          <div key={`per-photo-${i}`} data-page-row
               className="grid grid-cols-[64px_minmax(0,1fr)] items-center gap-[10px] rounded-[8px] bg-white/70 dark:bg-neutral-900/40 p-[8px]">
            <img src={cover(photo.url, 160, 160)} alt="" className="block h-[64px] w-[64px] rounded-[6px] object-cover" />
            <div className="grid gap-[4px]">
              <span className={LABEL}>{number}</span>
              {lineKey ? (
                <input aria-label={number} className={INPUT} maxLength={lineMax}
                       placeholder={lineKey === "pages" ? "What happens on this page" : "A line about this photo"}
                       value={(details[lineKey] || [])[i] || ""}
                       onChange={(e) => writeLine(i, e.target.value)} />
              ) : (
                <div className="grid grid-cols-2 gap-[8px]">
                  <input aria-label={`Name of person ${i + 1}`} className={INPUT} maxLength={s.people.name}
                         placeholder="Name"
                         value={(details.people || [])[i]?.name || ""}
                         onChange={(e) => writePerson(i, "name", e.target.value)} />
                  <input aria-label={`Label for person ${i + 1}`} className={INPUT} maxLength={s.people.label}
                         placeholder="e.g. Grandmother, or Rose bouquet"
                         value={(details.people || [])[i]?.label || ""}
                         onChange={(e) => writePerson(i, "label", e.target.value)} />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default StyleFields;
